import { LOCALE_COOKIE_NAME } from '@packages/domain/constants/cookies';
import { normalizeDayjsLocale } from '@packages/domain/utils/date/date';
import {
  isLatinAmericanSpanish,
  LATIN_AMERICAN_SPANISH_LOCALE,
} from '@packages/domain/utils/locale/latinAmericanSpanish';
import { windowUtils } from '@packages/domain/utils/windowUtils/windowUtils';
import { setLocale as setStoreLocale } from 'actions/locale/locale';
import { NOOP } from 'constants/actions';
import { DEFAULT_LOCALE } from 'constants/config';
import { FF_ENABLED_LOCALES } from 'constants/features';
import dayjs from 'dayjs';
import {
  createContext,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { BrowserRepository as browser } from 'repositories/browser/browser';
import { EmbeddedRepository as embedded } from 'repositories/embedded/embedded';
import { useDispatch } from 'store/configureStore';
import { cookies } from 'utils/cookies/cookies';
import {
  GATED_LOCALES,
  getKeyFromLocaleCode,
  isRtlLocale,
  isValidLocale,
  locales,
  type SupportedLocalesKeys,
  type Translations,
} from 'utils/translations/translations';
import EN from '../../utils/translations/locales/EN.json';

export interface TranslationContextInterface {
  locale: SupportedLocalesKeys;
  keys: Translations;
  fallbackKeys: Translations;
  setLocale: (locale: SupportedLocalesKeys) => void;
}

const loadLanguageFile = async (
  language: SupportedLocalesKeys,
): Promise<Translations> => {
  const key = locales[language];

  return await import(
    /* webpackChunkName: "locales/[request]" */ `utils/translations/locales/${key}.json`
  );
};

const defaultLanguageFile: Translations = EN;

const TranslationContext = createContext<TranslationContextInterface>({
  locale: DEFAULT_LOCALE as SupportedLocalesKeys,
  keys: defaultLanguageFile,
  fallbackKeys: defaultLanguageFile,
  setLocale: NOOP,
});

const getUrlLocale = (): string | null => {
  return new URLSearchParams(windowUtils.getSearch()).get('locale');
};

const toLatinAmericanSpanish = (locale: string) =>
  isLatinAmericanSpanish(locale) ? LATIN_AMERICAN_SPANISH_LOCALE : locale;

const getInitialLocale = (): SupportedLocalesKeys => {
  const embeddedLocale = toLatinAmericanSpanish(
    embedded.getProp('locale', '') as string,
  );
  const urlLocale = toLatinAmericanSpanish(getUrlLocale() || '');
  const previousLocale =
    getKeyFromLocaleCode(cookies.read(LOCALE_COOKIE_NAME) || '') || '';

  if (isValidLocale(embeddedLocale)) {
    return embeddedLocale as SupportedLocalesKeys;
  }
  if (isValidLocale(urlLocale)) {
    return urlLocale as SupportedLocalesKeys;
  }
  if (isValidLocale(previousLocale)) {
    return previousLocale as SupportedLocalesKeys;
  }

  return DEFAULT_LOCALE;
};

const isLocaleEnabled = async (locale: string) => {
  try {
    const { featureFlags } = await import('services/featureFlags/featureFlags');
    const flags = await featureFlags.fetch([FF_ENABLED_LOCALES], { locale });
    return flags[FF_ENABLED_LOCALES] === true;
  } catch {
    return false;
  }
};

const setLanguageInformation = (locale: string) => {
  document.documentElement.setAttribute('lang', locale);
  const isCookiePermanent = false;

  cookies.set(
    LOCALE_COOKIE_NAME,
    locale,
    isCookiePermanent,
    browser.getFlywireSubdomain(),
  );

  document.documentElement.setAttribute(
    'dir',
    isRtlLocale(locale) ? 'rtl' : 'ltr',
  );

  dayjs.locale(normalizeDayjsLocale(locale));
};

function TranslationProvider({ children }: { children: ReactNode }) {
  const dispatch = useDispatch();
  const { requestedLocale, initialLocale } = useMemo(() => {
    const requestedLocale = getInitialLocale();
    const initialLocale = GATED_LOCALES.includes(requestedLocale)
      ? DEFAULT_LOCALE
      : requestedLocale;
    dispatch(setStoreLocale(initialLocale));
    setLanguageInformation(initialLocale);
    return { requestedLocale, initialLocale };
  }, [dispatch]);
  const [locale, setLocale] = useState(initialLocale);
  const hasPickedLocale = useRef(false);
  const latestPickId = useRef(0);

  useEffect(() => {
    if (requestedLocale === initialLocale) {
      return;
    }
    let isCancelled = false;
    isLocaleEnabled(requestedLocale).then(isEnabled => {
      if (isEnabled && !isCancelled && !hasPickedLocale.current) {
        setLocale(requestedLocale);
        setLanguageInformation(requestedLocale);
        dispatch(setStoreLocale(requestedLocale));
      }
    });
    return () => {
      isCancelled = true;
    };
  }, [requestedLocale, initialLocale, dispatch]);
  const fallbackKeys = defaultLanguageFile;
  const [keys, setKeys] = useState(fallbackKeys);

  const loadLanguage = async (locale: SupportedLocalesKeys) => {
    setKeys(await loadLanguageFile(locale));
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: false positive
  useEffect(() => {
    loadLanguage(locale);
  }, [locale]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: false positive
  const value: TranslationContextInterface = useMemo(
    () => ({
      keys,
      fallbackKeys,
      locale,
      setLocale: (pickedLocale: SupportedLocalesKeys) => {
        latestPickId.current += 1;
        const pickId = latestPickId.current;
        const applyPickedLocale = () => {
          hasPickedLocale.current = true;
          setLocale(pickedLocale);
          setLanguageInformation(pickedLocale);
          dispatch(setStoreLocale(pickedLocale));
        };

        if (!GATED_LOCALES.includes(pickedLocale)) {
          applyPickedLocale();
          return;
        }
        isLocaleEnabled(pickedLocale).then(isEnabled => {
          if (isEnabled && pickId === latestPickId.current) {
            applyPickedLocale();
          }
        });
      },
    }),
    [keys, fallbackKeys, locale, setLocale, dispatch],
  );

  if (!Object.keys(locales).includes(locale)) {
    setLocale(DEFAULT_LOCALE);
  }

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
}

export { TranslationContext, TranslationProvider };
